/**
 * 組織課題の画面で使う純粋関数（看板への並べ方・人の表示・書き換えた項目だけの差分）。
 *
 * **並び順と権限は作らない。** 課題の並び（ピン留め → 優先度 → 更新の新しい順）は API が返した
 * 順をそのまま使い、動かせるか・編集できるかは API の `permissions` を見る。画面で並べ直したり
 * 判定し直したりすると、MCP から見た並び・権限と黙ってずれる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 組織課題の看板表示のための列分け・人の表示名・Next の要約・更新差分の計算
 * @graph-connects none
 */

import type {
  Action,
  Board,
  Column,
  HistoryEvent,
  Issue,
  IssuePatch,
} from '@cortex/domain-org-issues';
import type { OrgIssuePeople, OrgIssueView } from './api/org-issues.js';

/** 看板の 1 列と、そこに入る課題。 @graph-connects none */
export interface KanbanLane {
  column: Column;
  issues: OrgIssueView[];
}

/**
 * 列を左からの並びにして、課題を API の返した順のまま列に振り分ける。
 *
 * どの列にも当たらない課題（列を消した直後など）は捨てずに先頭の列へ寄せる。捨てると
 * 画面から消えたように見え、MCP からは見えるのに画面では見えない課題ができる。
 * @graph-connects none
 */
export function toKanbanLanes(board: Board, issues: readonly OrgIssueView[]): KanbanLane[] {
  const columns = [...board.columns].sort((a, b) => a.position - b.position);
  const lanes = columns.map((column) => ({ column, issues: [] as OrgIssueView[] }));
  const byId = new Map(lanes.map((lane) => [lane.column.id, lane]));
  for (const view of issues) {
    const lane = byId.get(view.issue.columnId) ?? lanes[0];
    lane?.issues.push(view);
  }
  return lanes;
}

/**
 * 人の表示名。ニックネーム → 名前 → `users.id` の順に使う（表に無い人も ID で出す）。
 * @graph-connects none
 */
export function personLabel(people: OrgIssuePeople, userId: string): string {
  const person = people[userId];
  if (person === undefined) return userId;
  const nickName = person.nickName?.trim() ?? '';
  return nickName !== '' ? nickName : person.name;
}

/** 人を選ぶ候補（Picker の形）。表示名の順に並べる。 @graph-connects none */
export function personOptions(
  people: OrgIssuePeople,
  userIds: readonly string[],
): { value: string; label: string; description: string }[] {
  return userIds
    .map((id) => ({
      value: id,
      label: personLabel(people, id),
      description: people[id]?.email ?? '',
    }))
    .sort((a, b) => a.label.localeCompare(b.label, 'ja'));
}

/** カードに出す Next の要約。 @graph-connects none */
export interface ActionSummary {
  done: number;
  total: number;
  /** 未完了の Next の一番近い期日（YYYY-MM-DD）。無ければ `null` */
  nextDueOn: string | null;
}

/** Next の進み具合と、未完了で一番近い期日。 @graph-connects none */
export function summarizeActions(actions: readonly Action[]): ActionSummary {
  const dues = actions
    .filter((action) => !action.done && action.dueOn !== null)
    .map((action) => action.dueOn ?? '')
    .sort();
  return {
    done: actions.filter((action) => action.done).length,
    total: actions.length,
    nextDueOn: dues[0] ?? null,
  };
}

/** 期日を過ぎているか（`today` は YYYY-MM-DD）。 @graph-connects none */
export function isOverdue(dueOn: string | null, today: string): boolean {
  return dueOn !== null && dueOn < today;
}

/** 手元の日付（YYYY-MM-DD）。 @graph-connects none */
export function localToday(now: Date = new Date()): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${String(now.getFullYear())}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** 下書きの Next 1 件。新しく足した Next は `id` を持たない（API が採番する）。 @graph-connects none */
export interface ActionDraft {
  id?: string;
  text: string;
  assigneeIds: string[];
  dueOn: string | null;
  done: boolean;
}

/** 課題の中身として画面で書き換える項目。 @graph-connects none */
export type IssueDraft = Required<Omit<IssuePatch, 'actions'>> & { actions: ActionDraft[] };

/** 課題の今の中身を、書き換えの下書きにする。 @graph-connects none */
export function toIssueDraft(issue: Issue): IssueDraft {
  return {
    title: issue.title,
    body: issue.body,
    priority: issue.priority,
    assignees: [...issue.assignees],
    actions: issue.actions.map((action) => ({
      id: action.id,
      text: action.text,
      assigneeIds: [...action.assigneeIds],
      dueOn: action.dueOn,
      done: action.done,
    })),
    labels: [...issue.labels],
    links: issue.links.map((link) => ({ ...link })),
    resolutionNote: issue.resolutionNote,
  };
}

/**
 * 下書きのうち、今の中身から変わった項目だけを返す。
 *
 * 全項目を送ると、別の人（や MCP）が同じ課題の別の項目を書き換えた直後に、古い値で
 * 上書きしてしまう。書き換えた項目だけを送れば、触っていない項目は相手の変更が残る。
 * @graph-connects none
 */
export function changedFields(issue: Issue, draft: IssueDraft): IssuePatch {
  const base = toIssueDraft(issue);
  const patch: Record<string, unknown> = {};
  for (const key of Object.keys(draft) as (keyof IssueDraft)[]) {
    if (JSON.stringify(draft[key]) !== JSON.stringify(base[key])) patch[key] = draft[key];
  }
  return patch as IssuePatch;
}

/** ラベルの入力（カンマ・読点区切り）を一覧にする。空と重複は落とす。 @graph-connects none */
export function parseLabels(text: string): string[] {
  const labels = text
    .split(/[,、]/)
    .map((label) => label.trim())
    .filter((label) => label !== '');
  return [...new Set(labels)];
}

/** 一覧の `index` の要素を `delta` だけ動かす（端を越えたらそのまま）。 @graph-connects none */
export function moveItem<T>(items: readonly T[], index: number, delta: number): T[] {
  const target = index + delta;
  if (target < 0 || target >= items.length) return [...items];
  const next = [...items];
  const [item] = next.splice(index, 1);
  if (item !== undefined) next.splice(target, 0, item);
  return next;
}

/** 履歴の値を表示に直すための材料。 @graph-connects none */
export interface HistoryFormatContext {
  columns: readonly Column[];
  people: OrgIssuePeople;
  t: (key: string) => string;
}

/**
 * 履歴の値（列 ID・優先度・共有状態・ピン留め・担当の `users.id` のカンマ区切り）を表示に直す。
 * 消した列は ID のまま出す（履歴は消した後も読めるようにしたい）。
 * @graph-connects none
 */
export function formatHistoryValue(
  field: HistoryEvent['field'],
  value: string | null,
  ctx: HistoryFormatContext,
): string {
  if (value === null || value === '') return ctx.t('orgIssues.common.none');
  switch (field) {
    case 'created':
    case 'column':
      return ctx.columns.find((column) => column.id === value)?.label ?? value;
    case 'priority':
      return ctx.t(`orgIssues.priority.${value}`);
    case 'sharing':
      return ctx.t(`orgIssues.sharing.${value}`);
    case 'pinned':
      return ctx.t(value === 'true' ? 'orgIssues.history.pinnedOn' : 'orgIssues.history.pinnedOff');
    case 'assignees':
      return value
        .split(',')
        .map((id) => personLabel(ctx.people, id))
        .join(', ');
  }
}

/** 看板の URL の search（開いている課題）。 @graph-connects none */
export interface BoardSearch {
  issue?: string;
}

/** 看板の URL の search を読む。空や文字列でない値は捨てる。 @graph-connects none */
export function parseBoardSearch(raw: Record<string, unknown>): BoardSearch {
  const issue = raw.issue;
  return typeof issue === 'string' && issue.trim() !== '' ? { issue: issue.trim() } : {};
}
